/**
 * @fileoverview Translate persisted agent output into readable findings and source evidence. Citation lookup connects known source metadata to rendered claims; support/verification states remain recorded evidence rather than new legal judgments. `IntakeAgentRunModals.js` supplies run context and `IntakeTabs.js` composes pipeline views. This layer formats existing output without retrieving authorities, executing agents, or changing staff decisions.
 */

import { useState } from 'react';
import { AppModal } from '../shared';
import AppIcon from '../shared/AppIcon';
import { formatPercent, titleize } from './intakeFormatters';

// Rendering for structured agent output JSON: prioritized field outlines, nested
// tables/records, and legal-source chips with their detail modal. Shared by the
// stage timeline, specialist accordions, and the agent run detail modal.

export const LEGAL_VERIFICATION_OUTPUT_KEYS = new Set([
  'legal_authority_findings',
  'unsupported_legal_claims',
  'research_tasks',
  'research_requests',
  'research_questions',
  'legal_research_summary',
  'legal_verification_preflight_sources',
  'legal_verification_preflight_status',
  'legal_verification_results',
  'legal_verification_cache_status',
  'citation_warnings',
  'citation_links',
]);
export const LEGAL_SOURCE_CHIP_ONLY_OUTPUT_KEYS = new Set([
  'legal_verification_preflight_sources',
  'legal_verification_results',
  'citation_links',
]);
export const LEGAL_AUTHORITY_MODAL_OUTPUT_KEYS = new Set([
  'legal_authority_findings',
]);



export function formatJsonBlock(value) {
  if (!value) return '';
  if (typeof value !== 'string') return JSON.stringify(value, null, 2);
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (error) {
    return value;
  }
}

const AGENT_OUTPUT_KEY_ORDER = [
'recommendation',
'recommended_action',
'classification',
'confidence',
'rejection_reason',
'categories',
'high_level_summary',
'summary',
'rationale',
'key_findings',
'client_info_needed',
'missing_info',
'next_actions',
'flag_for_human_review',
'flag_reasons'];

const AGENT_OUTPUT_METADATA_KEYS = new Set(['agent_id', 'lead_id', 'stage', 'timestamp']);
// Decision-relevant keys shown up front in timeline views; everything else collapses behind "more fields".
const PRIORITY_AGENT_OUTPUT_KEYS = new Set(AGENT_OUTPUT_KEY_ORDER);
const TIMELINE_HIDDEN_OUTPUT_KEYS = new Set([...AGENT_OUTPUT_METADATA_KEYS]);
const LEGACY_STAGE_REFERENCE_LABELS = [
  ['Stage 3A', 'Information Evaluation'],
  ['Stage 3B', 'Information Evaluation'],
  ['Stage 2A', 'Claim Routing/Assignment'],
  ['Stage 2B', 'Routed Claim Specialist'],
  ['Stage 2C', 'Jurisdiction and Arbitration'],
  ['Stage 2D', 'Defendant Intel'],
  ['Stage 1', 'Triage'],
  ['Stage 4', 'Document Review'],
  ['Stage 5', 'Final Evaluation']];



export function parseJsonValue(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}



function isEmptyOutputValue(value) {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}



function filterAgentOutputEntries(output, compact = false, { showLegalVerification = true } = {}) {
  return Object.entries(output).filter(

  ([key, entryValue]) => {
	    if (isEmptyOutputValue(entryValue)) return false;
	    if (AGENT_OUTPUT_METADATA_KEYS.has(key)) return false;
	    if (!showLegalVerification && LEGAL_VERIFICATION_OUTPUT_KEYS.has(key)) return false;
	    if (showLegalVerification && LEGAL_AUTHORITY_MODAL_OUTPUT_KEYS.has(key)) return false;
	    if (showLegalVerification && LEGAL_SOURCE_CHIP_ONLY_OUTPUT_KEYS.has(key)) return false;
	    if (!compact) return true;
	    return !TIMELINE_HIDDEN_OUTPUT_KEYS.has(key);
	  });
	}



function sortAgentOutputEntries(entries) {
  return entries.sort(

  ([leftKey], [rightKey]) => {
    const leftIndex = AGENT_OUTPUT_KEY_ORDER.indexOf(leftKey);
    const rightIndex = AGENT_OUTPUT_KEY_ORDER.indexOf(rightKey);
    if (leftIndex >= 0 || rightIndex >= 0) {
      return (leftIndex >= 0 ? leftIndex : 999) - (rightIndex >= 0 ? rightIndex : 999);
    }
    return titleize(leftKey).localeCompare(titleize(rightKey));
  });
}



function truncateSourceChipLabel(value, maxLength = 34) {
  const text = String(value || '').trim();
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(maxLength - 3, 1)).trim()}...`;
}



function getUrlHostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}



function addLegalSourceChip(chips, seen, source, fallbackIndex = 0) {
  if (!source) return;
  const isUrlString = typeof source === 'string' && /^https?:\/\//i.test(source);
  const sourceObject = typeof source === 'object' ? source : isUrlString ? { url: source } : { citation: source };
  const url = typeof source === 'string' && /^https?:\/\//i.test(source) ? source : sourceObject.url || sourceObject.source_url || sourceObject.href;
  const citation = sourceObject.citation || sourceObject.cite || sourceObject.citation_text;
  const title = sourceObject.title || sourceObject.name;
  const label = truncateSourceChipLabel(citation || title || getUrlHostname(url) || `Source ${fallbackIndex + 1}`);
  if (!label) return;
  const key = `${String(url || '').trim()}|${label}`.toLowerCase();
  const labelKey = `label:${label.toLowerCase()}`;
  if (seen.has(key) || seen.has(labelKey)) return;
  seen.add(key);
  seen.add(labelKey);
  chips.push({
    label,
    url,
    citation,
    sourceObject,
    rawSource: source,
    title: [citation, title, sourceObject.jurisdiction, sourceObject.source_type].filter(Boolean).join(' / ') || label,
  });
}



function collectLegalSourceChips(output) {
  if (!output || typeof output !== 'object') return [];
  const chips = [];
  const seen = new Set();
  const addSources =

  (sources) => {
    if (!Array.isArray(sources)) return;
    sources.forEach((source, index) => addLegalSourceChip(chips, seen, source, index));
  };
  const addCitations =

  (citations) => addSources((Array.isArray(citations) ? citations : [citations]).filter(Boolean));
  const addUrls =

  (urls) => addSources((Array.isArray(urls) ? urls : [urls]).filter(Boolean));

  addSources(output.sources);
  addUrls(output.source_urls);
  addCitations(output.citations);
  addSources(output.legal_verification_preflight_sources);

  (Array.isArray(output.legal_verification_results) ? output.legal_verification_results : []).forEach((result) => {
    addSources(result?.sources);
    addUrls(result?.source_urls);
    addCitations(result?.citations);
  });

  (Array.isArray(output.legal_authority_findings) ? output.legal_authority_findings : []).forEach(

  (finding) => {
    if (!finding || typeof finding !== 'object') return;
    addSources(finding.sources);
    addUrls(finding.source_urls);
    addCitations(finding.citations || finding.citation);
  });

  return chips.slice(0, 8);
}



function getLegalSourceDetailRows(source) {
  if (!source) return [];
  const sourceObject = source.sourceObject || {};
  return [
    ['Citation', source.citation || sourceObject.citation || sourceObject.cite || sourceObject.citation_text],
    ['Title', sourceObject.title || sourceObject.name],
    ['URL', source.url],
    ['Jurisdiction', sourceObject.jurisdiction],
    ['Source Type', sourceObject.source_type || sourceObject.type],
    ['Authority Weight', sourceObject.authority_weight],
    ['Verification', sourceObject.verification && titleize(sourceObject.verification)],
    ['Retrieved', sourceObject.retrieved_at || sourceObject.created_at],
    ['Status', sourceObject.status || sourceObject.support_status],
  ].filter(([, value]) => !isEmptyOutputValue(value));
}



function getLegalSourceLongText(source) {
  if (!source) return '';
  const raw = source.rawSource;
  if (typeof raw === 'string') return raw;
  const sourceObject = source.sourceObject || raw || {};
  const preferredKeys = ['excerpt', 'excerpts', 'summary', 'explanation', 'description', 'snippet', 'text', 'content', 'body', 'notes'];
  const parts = preferredKeys.flatMap(

  (key) => {
    const value = sourceObject[key];
    if (isEmptyOutputValue(value)) return [];
    if (Array.isArray(value)) return value.map((item) => typeof item === 'object' ? formatJsonBlock(item) : String(item));
    return [typeof value === 'object' ? formatJsonBlock(value) : String(value)];
  });
  return parts.length ? parts.join('\n\n') : formatJsonBlock(sourceObject);
}



export function LegalSourceDetailModal({ source, onClose }) {
  const rows = getLegalSourceDetailRows(source);
  const detailText = getLegalSourceLongText(source);
  return (
    <AppModal
      visible={Boolean(source)}
      title={source?.label || 'Legal Source'}
      description={source?.title || 'Legal verification source detail'}
      onClose={onClose}
      width="min(760px, calc(100vw - 2rem))"
      className="intake-legal-source-modal">
      {source &&
        <div className="intake-legal-source-detail">
          {rows.length > 0 &&
            <dl className="intake-legal-source-detail__meta">
              {rows.map(([label, value]) =>
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{label === 'URL' && source.url ?
                    <a href={source.url} target="_blank" rel="noreferrer">{source.url}</a> :
                    String(value)}
                  </dd>
                </div>
              )}
            </dl>}
          {source.url &&
            <a className="intake-button intake-button--quiet intake-legal-source-detail__open" href={source.url} target="_blank" rel="noreferrer">
              <AppIcon icon="pi pi-external-link" aria-hidden="true" />
              <span>Open Source</span>
            </a>}
          {detailText &&
            <pre className="intake-legal-source-detail__text">{detailText}</pre>}
        </div>}
    </AppModal>
  );
}



export function LegalSourceChips({ output, chips: providedChips = null, showLegalVerification = true, onSourceSelect = () => {}, className = '' }) {
  if (!showLegalVerification) return null;
  const chips = providedChips || collectLegalSourceChips(output);
  if (!chips.length) return null;
  return (
    <div className={`intake-agent-output__legal-sources${className ? ` ${className}` : ''}`} aria-label="Legal sources">
      {chips.map((chip) =>
        <button
          type="button"
          className="intake-agent-output-source-chip"
          title={chip.title || chip.label}
          key={`${chip.url || chip.title || chip.label}-${chip.label}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSourceSelect(chip);
          }}>
          {chip.label}
        </button>
      )}
    </div>
  );
}



function getLegalAuthorityFindingObject(finding) {
  if (finding && typeof finding === 'object' && !Array.isArray(finding)) return finding;
  return { proposition: finding };
}



function getFirstLegalAuthorityValue(finding, keys) {
  const foundKey = keys.find((key) => !isEmptyOutputValue(finding[key]));
  return foundKey ? finding[foundKey] : '';
}



function getLegalAuthorityPrimaryText(finding, index) {
  const primaryValue = getFirstLegalAuthorityValue(finding, [
    'proposition',
    'claim',
    'issue',
    'finding',
    'question',
    'summary',
    'explanation',
    'citation',
  ]);
  if (Array.isArray(primaryValue)) return primaryValue.filter(Boolean).join(', ') || `Finding ${index + 1}`;
  if (primaryValue && typeof primaryValue === 'object') return formatJsonBlock(primaryValue);
  return primaryValue ? String(primaryValue) : `Finding ${index + 1}`;
}



function getLegalAuthoritySupportStatus(finding) {
  return getFirstLegalAuthorityValue(finding, ['support_status', 'status', 'support']);
}



function getLegalAuthoritySupportTone(status) {
  const normalized = String(status || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['supported', 'verified', 'found'].includes(normalized)) return 'success';
  if (['partially_supported', 'partial', 'mixed', 'needs_review'].includes(normalized)) return 'warning';
  if (['contradicted', 'unsupported', 'not_supported', 'invalid'].includes(normalized)) return 'danger';
  return 'neutral';
}



function getLegalAuthoritySupportLabel(status, index) {
  return status ? titleize(status) : `Finding ${index + 1}`;
}



function isUrlValue(value) {
  return /^https?:\/\//i.test(String(value || '').trim());
}



function cleanCitationPart(value) {
  return String(value || '').trim().replace(/[.,;]+$/g, '');
}



function normalizeCitationKey(value) {
  return cleanCitationPart(value).replace(/\s+/g, ' ').toLowerCase();
}



function getSourceUrlFromObject(source) {
  if (!source || typeof source !== 'object') return '';
  return source.url || source.source_url || source.href || source.link || '';
}



function getCitationTextFromObject(source) {
  if (!source || typeof source !== 'object') return '';
  return source.citation || source.cite || source.citation_text || source.title || source.name || '';
}

// Citation URLs are never constructed on the frontend; the backend legal
// research service owns citation-to-URL building and ships it via source
// records and the `citation_links` output map.


function addCitationSourceMapping(map, citation, url) {
  const label = cleanCitationPart(citation);
  const citationKey = normalizeCitationKey(label);
  const sourceUrl = String(url || '').trim();
  if (!citationKey || !isUrlValue(sourceUrl) || map.has(citationKey)) return;
  map.set(citationKey, { url: sourceUrl, label });
}



function addCitationLinksFromOutput(map, links) {
  if (!links || typeof links !== 'object' || Array.isArray(links)) return;
  Object.entries(links).forEach(([citation, url]) => addCitationSourceMapping(map, citation, url));
}



function addCitationSourceMappingsFromSource(map, source) {
  if (!source) return;
  if (typeof source === 'string') return;
  if (Array.isArray(source)) {
    source.forEach((item) => addCitationSourceMappingsFromSource(map, item));
    return;
  }
  if (typeof source !== 'object') return;
  const url = getSourceUrlFromObject(source);
  const citation = getCitationTextFromObject(source);
  addCitationSourceMapping(map, citation, url);
}



function asOutputArray(value) {
  if (value === null || value === undefined || value === '') return [];
  return Array.isArray(value) ? value : [value];
}



function collectCitationSourceMapFromOutput(output) {
  const map = new Map();
  if (!output || typeof output !== 'object') return map;

  addCitationLinksFromOutput(map, output.citation_links);
  [
    output.sources,
    output.legal_verification_preflight_sources,
  ].forEach((sources) => addCitationSourceMappingsFromSource(map, sources));

  (Array.isArray(output.legal_verification_results) ? output.legal_verification_results : []).forEach((result) => {
    addCitationSourceMappingsFromSource(map, result?.sources);
  });

  (Array.isArray(output.legal_authority_findings) ? output.legal_authority_findings : []).forEach((finding) => {
    addCitationSourceMappingsFromSource(map, finding?.sources);
  });

  return map;
}



function collectCitationSourceMapFromFinding(finding, inheritedMap = null) {
  const map = new Map(inheritedMap instanceof Map ? inheritedMap : []);
  if (!finding || typeof finding !== 'object') return map;

  addCitationSourceMappingsFromSource(map, finding.sources);

  const citations = asOutputArray(finding.citations || finding.citation || finding.authorities || finding.authority);
  const sourceUrls = asOutputArray(finding.source_urls || finding.source_url || finding.urls || finding.url);
  citations.forEach((citation, index) => {
    if (citation && typeof citation === 'object') {
      addCitationSourceMappingsFromSource(map, citation);
      return;
    }
    addCitationSourceMapping(map, citation, sourceUrls[index]);
  });

  return map;
}



function getCitationSourceUrl(citation, citationSourceMap) {
  const text = cleanCitationPart(citation);
  if (!text) return '';
  if (isUrlValue(text)) return text;
  return citationSourceMap?.get?.(normalizeCitationKey(text))?.url || '';
}



function getCitationItemFromValue(value, citationSourceMap) {
  if (value && typeof value === 'object') {
    const label = getCitationTextFromObject(value) || getSourceUrlFromObject(value) || formatJsonBlock(value);
    const url = getSourceUrlFromObject(value) || getCitationSourceUrl(label, citationSourceMap);
    return { label, url };
  }
  const label = cleanCitationPart(value);
  return { label, url: getCitationSourceUrl(label, citationSourceMap) };
}



function getCitationItems(value, citationSourceMap) {
  if (Array.isArray(value)) {
    return value
      .filter((item) => !isEmptyOutputValue(item))
      .map((item) => getCitationItemFromValue(item, citationSourceMap))
      .filter((item) => item.label);
  }
  if (value && typeof value === 'object') {
    return [getCitationItemFromValue(value, citationSourceMap)].filter((item) => item.label);
  }
  const text = String(value || '').trim();
  if (!text) return [];
  const directItem = getCitationItemFromValue(text, citationSourceMap);
  if (directItem.url) return [directItem];
  const knownItems = findKnownCitationItemsInText(text, citationSourceMap);
  if (knownItems.length) return knownItems;
  return [directItem];
}

// Free-text citation values are segmented by matching against citations the
// backend already linked, not by parsing citation formats locally.


function findKnownCitationItemsInText(text, citationSourceMap) {
  if (!citationSourceMap?.size) return [];
  const normalizedText = normalizeCitationKey(text);
  const items = [];
  citationSourceMap.forEach((entry, key) => {
    const position = normalizedText.indexOf(key);
    if (position < 0) return;
    items.push({ label: entry.label, url: entry.url, position });
  });
  return items
    .sort((left, right) => left.position - right.position)
    .map(({ label, url }) => ({ label, url }));
}



function isCitationLabel(label) {
  return /citation|authorit/i.test(label || '');
}



function LegalAuthorityCitationValue({ value, citationSourceMap }) {
  const items = getCitationItems(value, citationSourceMap);
  if (!items.length) return null;
  return (
    <span className="intake-legal-authority-detail__citation-links">
      {items.map((item, index) =>
        item.url ?
          <a key={`${item.label}-${item.url}-${index}`} href={item.url} target="_blank" rel="noreferrer">{item.label}</a> :
          <span key={`${item.label}-${index}`}>{item.label}</span>
      )}
    </span>
  );
}



function getFindingCitationKeys(finding) {
  const citations = asOutputArray(finding.citations || finding.citation || finding.authorities || finding.authority);
  return new Set(
    citations
      .map((citation) => normalizeCitationKey(typeof citation === 'object' ? getCitationTextFromObject(citation) : citation))
      .filter(Boolean)
  );
}



function getFindingCitationWarnings(output, finding, index) {
  const warnings = Array.isArray(output?.citation_warnings) ? output.citation_warnings : [];
  if (!warnings.length) return [];
  const citationKeys = getFindingCitationKeys(finding);
  return warnings.filter(

  (warning) => {
    if (!warning || typeof warning !== 'object') return false;
    if (Number.isInteger(warning.finding_index) && warning.finding_index === index) return true;
    const warningKey = normalizeCitationKey(warning.citation || warning.citation_text || '');
    return Boolean(warningKey) && citationKeys.has(warningKey);
  });
}



export function collectLegalAuthorityFindingChips(output) {
  if (!output || typeof output !== 'object') return [];
  const findings = Array.isArray(output.legal_authority_findings) ? output.legal_authority_findings : [];
  const citationSourceMap = collectCitationSourceMapFromOutput(output);
  return findings
    .filter((finding) => !isEmptyOutputValue(finding))
    .map(

    (finding, index) => {
      const findingObject = getLegalAuthorityFindingObject(finding);
      const supportStatus = getLegalAuthoritySupportStatus(findingObject);
      const primaryText = getLegalAuthorityPrimaryText(findingObject, index);
      const citationWarnings = getFindingCitationWarnings(output, findingObject, index);
      return {
        index,
        label: truncateSourceChipLabel(primaryText, 58),
        supportLabel: getLegalAuthoritySupportLabel(supportStatus, index),
        tone: getLegalAuthoritySupportTone(supportStatus),
        title: primaryText,
        finding: findingObject,
        rawFinding: finding,
        citationSourceMap,
        citationWarnings,
      };
    });
}



export function LegalAuthorityFindingChips({ output, chips: providedChips = null, showLegalVerification = true, onFindingSelect = () => {}, className = '' }) {
  if (!showLegalVerification) return null;
  const chips = providedChips || collectLegalAuthorityFindingChips(output);
  if (!chips.length) return null;
  return (
    <div className={`intake-agent-output__authority-findings${className ? ` ${className}` : ''}`} aria-label="Legal authority findings">
      {chips.map((chip) =>
        <button
          type="button"
          className={`intake-agent-output-authority-chip intake-agent-output-authority-chip--${chip.tone}`}
          title={chip.title}
          aria-label={`Open legal authority finding ${chip.index + 1}: ${chip.title}${chip.citationWarnings?.length ? ' (citation unverified)' : ''}`}
          key={`${chip.index}-${chip.supportLabel}-${chip.label}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onFindingSelect(chip);
          }}>
          <span className="intake-agent-output-authority-chip__status">{chip.supportLabel}</span>
          {chip.citationWarnings?.length > 0 &&
            <span className="intake-agent-output-authority-chip__alert">Citation unverified</span>}
          <span className="intake-agent-output-authority-chip__label">{chip.label}</span>
        </button>
      )}
    </div>
  );
}



function getLegalAuthorityFindingRows(chip) {
  if (!chip?.finding) return [];
  const finding = chip.finding;
  return [
    ['Proposition', getFirstLegalAuthorityValue(finding, ['proposition', 'claim', 'issue', 'finding', 'question'])],
    ['Support Status', chip.supportLabel],
    ['Jurisdiction', finding.jurisdiction],
    ['Citations', finding.citations || finding.citation || finding.authorities || finding.authority],
    ['Source URLs', finding.source_urls || finding.source_url || finding.urls || finding.url],
  ].filter(([, value]) => !isEmptyOutputValue(value));
}

const LEGAL_AUTHORITY_NARRATIVE_KEYS = ['explanation', 'analysis', 'rationale', 'reasoning', 'summary', 'notes'];
const LEGAL_AUTHORITY_DETAIL_META_KEYS = new Set([
  'proposition',
  'claim',
  'issue',
  'finding',
  'question',
  'support_status',
  'status',
  'support',
  'jurisdiction',
  'citations',
  'citation',
  'authorities',
  'authority',
  'source_urls',
  'source_url',
  'urls',
  'url',
  ...LEGAL_AUTHORITY_NARRATIVE_KEYS,
]);



function getLegalAuthorityFindingNarrative(finding) {
  const parts = LEGAL_AUTHORITY_NARRATIVE_KEYS.flatMap(

  (key) => {
    const value = finding[key];
    if (isEmptyOutputValue(value)) return [];
    if (Array.isArray(value)) return value.map((item) => typeof item === 'object' ? formatJsonBlock(item) : String(item));
    return [typeof value === 'object' ? formatJsonBlock(value) : String(value)];
  });
  return parts.join('\n\n');
}



function getLegalAuthorityFindingAdditionalDetails(finding) {
  return Object.fromEntries(
    Object.entries(finding)
      .filter(([key, value]) => !LEGAL_AUTHORITY_DETAIL_META_KEYS.has(key) && !isEmptyOutputValue(value))
  );
}



function LegalAuthorityDetailValue({ label, value, citationSourceMap }) {
  if (isCitationLabel(label)) {
    return <LegalAuthorityCitationValue value={value} citationSourceMap={citationSourceMap} />;
  }
  if (Array.isArray(value)) {
    const values = value.filter((item) => !isEmptyOutputValue(item));
    if (/url/i.test(label)) {
      return (
        <>
          {values.map((item) =>
            <a key={String(item)} href={String(item)} target="_blank" rel="noreferrer">{String(item)}</a>
          )}
        </>
      );
    }
    return values.map((item) => typeof item === 'object' ? formatJsonBlock(item) : String(item)).join(', ');
  }
  if (value && typeof value === 'object') return <pre>{formatJsonBlock(value)}</pre>;
  if (/url/i.test(label) && value) return <a href={String(value)} target="_blank" rel="noreferrer">{String(value)}</a>;
  return String(value);
}



export function LegalAuthorityFindingDetailModal({ finding, onClose }) {
  const rows = getLegalAuthorityFindingRows(finding);
  const narrative = finding?.finding ? getLegalAuthorityFindingNarrative(finding.finding) : '';
  const additionalDetails = finding?.finding ? getLegalAuthorityFindingAdditionalDetails(finding.finding) : {};
  const citationSourceMap = finding?.finding ? collectCitationSourceMapFromFinding(finding.finding, finding.citationSourceMap) : new Map();
  const hasAdditionalDetails = Object.keys(additionalDetails).length > 0;
  return (
    <AppModal
      visible={Boolean(finding)}
      title={finding?.supportLabel || 'Legal Authority Finding'}
      description={finding?.title || 'Legal authority finding detail'}
      onClose={onClose}
      width="min(760px, calc(100vw - 2rem))"
      className="intake-legal-authority-modal">
      {finding &&
        <div className="intake-legal-authority-detail">
          {finding.citationWarnings?.length > 0 &&
            <section className="intake-legal-authority-detail__warnings" aria-label="Citation warnings">
              {finding.citationWarnings.map((warning, index) =>
                <p key={`${warning.citation || 'warning'}-${index}`}>
                  <strong>{warning.citation || 'Citation'}:</strong> {warning.reason || 'This citation could not be verified against official sources.'}
                </p>
              )}
            </section>}
          {rows.length > 0 &&
            <dl className="intake-legal-authority-detail__meta">
              {rows.map(([label, value]) =>
                <div key={label}>
                  <dt>{label}</dt>
                  <dd><LegalAuthorityDetailValue label={label} value={value} citationSourceMap={citationSourceMap} /></dd>
                </div>
              )}
            </dl>}
          {narrative &&
            <section className="intake-legal-authority-detail__section">
              <h3>Explanation</h3>
              <pre>{narrative}</pre>
            </section>}
          {hasAdditionalDetails &&
            <section className="intake-legal-authority-detail__section">
              <h3>Additional Details</h3>
              <pre>{formatJsonBlock(additionalDetails)}</pre>
            </section>}
        </div>}
    </AppModal>
  );
}



function formatAgentPrimitive(value, fieldKey = '') {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const numericValue = Number(value);
  if (/confidence|score|probability/i.test(fieldKey) && !Number.isNaN(numericValue) && numericValue >= 0 && numericValue <= 1) {
    return formatPercent(numericValue);
  }
  return formatLegacyStageReferences(String(value));
}



export function formatLegacyStageReferences(value) {
  if (typeof value !== 'string' || !value) return value;
  return LEGACY_STAGE_REFERENCE_LABELS.reduce(
    (text, [legacyLabel, replacement]) => text.replace(new RegExp(`\\b${legacyLabel}\\b`, 'gi'), replacement),
    value
  );
}



function parseBooleanOutput(value) {
  if (typeof value === 'boolean') return value;
  const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['yes', 'true', 'required', 'review_required'].includes(normalized)) return true;
  if (['no', 'false', 'not_required', 'none'].includes(normalized)) return false;
  return null;
}



function isAgentOutputObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}



function isPrimitiveAgentOutputValue(value) {
  return value === null || value === undefined || typeof value !== 'object';
}



function isScalarOutputArray(value) {
  return Array.isArray(value) && value.every((item) => isPrimitiveAgentOutputValue(item));
}



function shouldRenderArrayAsChips(value) {
  return isScalarOutputArray(value) && value.every((item) => {
    const text = formatAgentPrimitive(item);
    return text.length <= 42 && !text.includes('\n');
  });
}



function getAgentOutputTableColumns(rows) {
  if (!Array.isArray(rows) || rows.length < 2 || rows.some((row) => !isAgentOutputObject(row))) return [];
  if (rows.some((row) => Object.values(row).some((value) => !isEmptyOutputValue(value) && typeof value === 'object'))) return [];
  const columns = [];
  rows.forEach((row) => {
    Object.entries(row).forEach(([key, value]) => {
      if (AGENT_OUTPUT_METADATA_KEYS.has(key) || isEmptyOutputValue(value) || columns.includes(key)) return;
      columns.push(key);
    });
  });
  if (columns.length < 2 || columns.length > 8) return [];
  return sortAgentOutputEntries(columns.map((key) => [key, true])).map(([key]) => key);
}



function AgentOutputTable({ rows, columns, depth }) {
  return (
    <div className="intake-agent-output__table-wrap">
      <table className="intake-agent-output__table">
        <thead>
          <tr>
            {columns.map((column) => <th key={column}>{titleize(column)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) =>
            <tr key={`row-${rowIndex}`}>
              {columns.map((column) =>
                <td key={column}>
                  <AgentOutputValue value={row[column]} fieldKey={column} depth={depth + 1} />
                </td>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}



function AgentOutputScalarArray({ items, fieldKey }) {
  if (shouldRenderArrayAsChips(items)) {
    return (
      <div className="intake-agent-output__chips">
        {items.map((item, index) =>
          <span className="intake-agent-output-chip" key={`${fieldKey}-${index}`}>
            {formatAgentPrimitive(item, fieldKey)}
          </span>
        )}
      </div>
    );
  }
  return (
    <ul className="intake-agent-output__list">
      {items.map((item, index) =>
        <li key={`${fieldKey}-${index}`}>{formatAgentPrimitive(item, fieldKey)}</li>
      )}
    </ul>
  );
}



function AgentOutputObjectSection({ entries, title = '', depth = 0 }) {
  if (!entries.length) return <span className="intake-agent-output__empty">-</span>;
  return (
    <section className="intake-agent-output__section" data-depth={depth}>
      {title && <h4>{title}</h4>}
      <div className="intake-agent-output__section-fields">
        {entries.map(([key, entryValue]) =>
          <div className="intake-agent-output__section-row" key={key}>
            <span>{titleize(key)}</span>
            <div>
              <AgentOutputValue value={entryValue} fieldKey={key} depth={depth + 1} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}



function AgentOutputRecordList({ items, fieldKey, depth }) {
  return (
    <div className="intake-agent-output__record-list">
      {items.map((item, index) => {
        const itemEntries = isAgentOutputObject(item) ?
          sortAgentOutputEntries(Object.entries(item).filter(([key, entryValue]) => !AGENT_OUTPUT_METADATA_KEYS.has(key) && !isEmptyOutputValue(entryValue))) :
          [];
        return (
          <section className="intake-agent-output__record" key={`${fieldKey}-${index}`}>
            <h4>{titleize(fieldKey)} {index + 1}</h4>
            {itemEntries.length ?
              <AgentOutputObjectSection entries={itemEntries} depth={depth + 1} /> :
              <AgentOutputValue value={item} fieldKey={fieldKey} depth={depth + 1} />}
          </section>
        );
      })}
    </div>
  );
}



function AgentOutputValue({ value, fieldKey = '', depth = 0 }) {
  if (isEmptyOutputValue(value)) return <span className="intake-agent-output__empty">-</span>;
  if (fieldKey === 'flag_for_human_review' || fieldKey === 'human_review_required') {
    const humanReview = parseBooleanOutput(value);
    if (humanReview !== null) {
      return (
        <span className={`intake-agent-output-chip intake-agent-output-chip--${humanReview ? 'warning' : 'neutral'}`}>
          {humanReview ? 'Advisory flag' : 'No advisory flag'}
        </span>);
    }
  }
  if (Array.isArray(value)) {
    const tableColumns = getAgentOutputTableColumns(value);
    if (tableColumns.length) {
      return <AgentOutputTable rows={value} columns={tableColumns} depth={depth} />;
    }
    if (isScalarOutputArray(value)) {
      return <AgentOutputScalarArray items={value} fieldKey={fieldKey} />;
    }
    return <AgentOutputRecordList items={value} fieldKey={fieldKey || 'Item'} depth={depth} />;
  }
  if (typeof value === 'object') {
    const entries = sortAgentOutputEntries(
      Object.entries(value).filter(([key, entryValue]) => !AGENT_OUTPUT_METADATA_KEYS.has(key) && !isEmptyOutputValue(entryValue))
    );
    return <AgentOutputObjectSection entries={entries} depth={depth} />;
  }
  return <span>{formatAgentPrimitive(value, fieldKey)}</span>;
}



export function FormattedAgentOutput({
  value,
  title = 'Formatted Output',
  compact = false,
  showTitle = true,
  showLegalVerification = true,
  showSourceChips = true,
  showAuthorityFindingChips = true,
  onSourceSelect = () => {}
}) {
  const [selectedLegalAuthorityFinding, setSelectedLegalAuthorityFinding] = useState(null);
  const output = parseJsonValue(value);
  if (!output || typeof output !== 'object') return null;
  const entries = sortAgentOutputEntries(filterAgentOutputEntries(output, compact, { showLegalVerification }));
  const authorityFindingItems = showAuthorityFindingChips && showLegalVerification ? collectLegalAuthorityFindingChips(output) : [];
  const sourceChipItems = showSourceChips && showLegalVerification ? collectLegalSourceChips(output) : [];
  if (!entries.length && !authorityFindingItems.length && !sourceChipItems.length) return null;
  const priorityEntries = compact ? entries.filter(([key]) => PRIORITY_AGENT_OUTPUT_KEYS.has(key)) : entries;
  const secondaryEntries = compact ? entries.filter(([key]) => !PRIORITY_AGENT_OUTPUT_KEYS.has(key)) : [];
  const collapseSecondaryEntries = compact && priorityEntries.length > 0 && secondaryEntries.length >= 2;
  const visibleEntries = collapseSecondaryEntries ? priorityEntries : entries;
  const renderEntries =

  (entryList) => entryList.map(([key, entryValue]) => {
    const wide = Array.isArray(entryValue) || typeof entryValue === 'object' || String(entryValue || '').length > 90;
    return (
      <div className={`intake-agent-output__field ${wide ? 'intake-agent-output__field--wide' : ''}`} key={key}>
        <span>{titleize(key)}</span>
        <div>
          <AgentOutputValue value={entryValue} fieldKey={key} />
        </div>
      </div>);
  });

  return (
    <section className={`intake-agent-output ${compact ? 'intake-agent-output--timeline' : ''} ${showTitle ? '' : 'intake-agent-output--no-title'}`} aria-label={`${title} output`}>
      {showTitle && <div className="intake-agent-output__header">
        <h3>{title}</h3>
      </div>}
      {visibleEntries.length > 0 &&
      <div className="intake-agent-output__outline">
          {renderEntries(visibleEntries)}
        </div>
      }
      {collapseSecondaryEntries &&
      <details className="intake-agent-output__more">
        <summary>Show {secondaryEntries.length} more fields</summary>
        <div className="intake-agent-output__outline">
          {renderEntries(secondaryEntries)}
        </div>
      </details>
      }
      {authorityFindingItems.length > 0 &&
        <LegalAuthorityFindingChips
          chips={authorityFindingItems}
          showLegalVerification={showLegalVerification}
          onFindingSelect={setSelectedLegalAuthorityFinding} />}
      {sourceChipItems.length > 0 &&
        <LegalSourceChips
          chips={sourceChipItems}
          showLegalVerification={showLegalVerification}
          onSourceSelect={onSourceSelect} />}
      <LegalAuthorityFindingDetailModal
        finding={selectedLegalAuthorityFinding}
        onClose={() => setSelectedLegalAuthorityFinding(null)} />
    </section>);
}

export const STAGE_TIMELINE_HIDDEN_OUTPUT_KEYS = new Set([
  'agent_id',
  'lead_id',
  'stage',
  'timestamp',
  'skipped_agents',
  'queued_agent_run_ids',
  'waiting_agent_run_ids'
]);



export function formatStageValue(value) {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) {
    if (value.every((entry) => entry === null || typeof entry !== 'object')) {
      return value.filter(Boolean).join(', ');
    }
    return value
    .map(formatStructuredListItem)
    .filter(Boolean)
    .join('\n');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    return Object.entries(value)
    .filter(([key, entryValue]) => !STAGE_TIMELINE_HIDDEN_OUTPUT_KEYS.has(key) && entryValue !== null && entryValue !== undefined && entryValue !== '')
    .map(([key, entryValue]) => `${titleize(key)}: ${formatStageValue(entryValue)}`)
    .join(' / ');
  }
  return formatLegacyStageReferences(String(value));
}



export function formatStructuredListItem(value) {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) return formatStageValue(value);
  if (typeof value !== 'object') return formatLegacyStageReferences(String(value));

  const primaryKey = [
  'item',
  'document',
  'question',
  'title',
  'name',
  'label',
  'summary',
  'description',
  'text'
  ].find((key) => value[key] !== null && value[key] !== undefined && value[key] !== '');
  const primaryValue = primaryKey ? formatStageValue(value[primaryKey]) : '';
  const detailParts = Object.entries(value)
  .filter(([key, entryValue]) =>
  key !== primaryKey &&
  !STAGE_TIMELINE_HIDDEN_OUTPUT_KEYS.has(key) &&
  entryValue !== null &&
  entryValue !== undefined &&
  entryValue !== ''
  )
  .map(([key, entryValue]) => `${titleize(key)}: ${formatStageValue(entryValue)}`)
  .filter(Boolean);
  return [primaryValue].concat(detailParts).filter(Boolean).join(' / ');
}
